import { Gavel, ChevronRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { voteScores } from "@/lib/format";

export default function MatchVotesDialog({ match, round, votes, advancers, onClose }) {
  const open = !!match;
  const names = match ? match.participants || [match.a, match.b].filter(Boolean) : [];
  const judges = votes.map((v) => ({
    judge: v.judgeName || "Juez",
    scores: voteScores(v),
  }));
  const totals = names.map((n) => ({
    name: n,
    total: judges.reduce((s, j) => s + (j.scores[n] || 0), 0),
    advanced: advancers.has(n),
  }));
  const maxTotal = Math.max(...totals.map((t) => t.total), 1);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="match-votes-dialog"
        className="max-w-2xl border-[#1E293B] bg-[#0F172A] p-0 text-white"
      >
        <DialogHeader className="border-b border-[#1E293B] px-6 py-5">
          <div className="flex items-center gap-2 font-ui text-[10px] font-bold uppercase tracking-[0.25em] text-[#FF5500]">
            <Gavel className="h-3.5 w-3.5" />
            {round} · {match?.label || match?.group || "Enfrentamiento"}
          </div>
          <DialogTitle className="font-display text-3xl tracking-wider text-white">
            {names.join("  vs  ")}
          </DialogTitle>
          <DialogDescription className="font-body text-xs text-[#94A3B8]">
            {judges.length === 0
              ? "Sin votación registrada para este enfrentamiento."
              : `${judges.length} juez${judges.length === 1 ? "" : "es"} · puntuación acumulada`}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5 px-6 py-5">
          <ul className="flex flex-col gap-3">
            {totals.map((t) => (
              <li key={t.name} data-testid={`dialog-total-${t.name}`} className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2 font-body text-sm font-semibold">
                    {t.advanced && <ChevronRight className="h-4 w-4 text-[#FF5500]" />}
                    <span className={t.advanced ? "text-white" : "text-[#94A3B8]"}>{t.name}</span>
                    {t.advanced && (
                      <span className="bg-[#FF5500] px-1.5 py-0.5 font-ui text-[9px] font-bold uppercase tracking-[0.2em] text-white">
                        Avanza
                      </span>
                    )}
                  </span>
                  <span className={`font-display text-2xl tracking-wider ${t.advanced ? "text-[#FF5500]" : "text-[#94A3B8]"}`}>
                    {judges.length ? t.total.toFixed(1) : "–"}
                  </span>
                </div>
                <div className="h-1.5 w-full bg-[#080D1A]">
                  <div
                    className="h-full bg-[#FF5500]"
                    style={{ width: `${(t.total / maxTotal) * 100}%`, transition: "width 400ms ease" }}
                  />
                </div>
              </li>
            ))}
          </ul>

          {judges.length > 0 && (
            <div className="overflow-x-auto border border-[#1E293B]">
              <table data-testid="judge-votes-table" className="w-full text-left">
                <thead className="bg-[#080D1A]">
                  <tr>
                    <th className="px-3 py-2 font-ui text-[10px] font-bold uppercase tracking-[0.2em] text-[#94A3B8]">Juez</th>
                    {names.map((n) => (
                      <th key={n} className="px-3 py-2 text-right font-ui text-[10px] font-bold uppercase tracking-[0.2em] text-[#94A3B8]">{n}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1E293B]">
                  {judges.map((j, i) => {
                    const best = Math.max(...names.map((n) => j.scores[n] || 0));
                    return (
                      <tr key={i}>
                        <td className="px-3 py-2 font-body text-sm text-white">{j.judge}</td>
                        {names.map((n) => {
                          const s = j.scores[n] || 0;
                          return (
                            <td key={n} className={`px-3 py-2 text-right font-display text-lg tracking-wider ${s === best && s > 0 ? "text-[#FF5500]" : "text-[#94A3B8]"}`}>
                              {s.toFixed(1)}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
